import { getActiveValuesByTypeQuery, getOpenJobCountsByCityQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { useEffect, useState } from 'react';

export interface ServedCities {
  /** Active CITY metadata: the only cities a promo code may target. */
  cities: string[];
  /** Open jobs per city, for choosing the ones that need jobs. Missing while loading or on failure. */
  openJobs: Record<string, number>;
  loading: boolean;
}

/**
 * The served cities (Settings, type CITY) and how many open jobs each has. The counts are advice:
 * a failure leaves them empty and the picker still works.
 */
export const useServedCities = (): ServedCities => {
  const [cities, setCities] = useState<string[]>([]);
  const [openJobs, setOpenJobs] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchGraphQLApiData(getActiveValuesByTypeQuery('CITY'));
        const list: string[] = res?.data?.getActiveValuesByType ?? [];
        if (cancelled) return;
        setCities(list);
        if (list.length) {
          try {
            const counts = await fetchGraphQLApiData(getOpenJobCountsByCityQuery(list));
            const byCity: Record<string, number> = {};
            list.forEach((city, i) => {
              const n = Number(counts?.data?.[`c${i}`]);
              if (Number.isFinite(n)) byCity[city] = n;
            });
            if (!cancelled) setOpenJobs(byCity);
          } catch {
            /* Advice only; the picker works without it. */
          }
        }
      } catch {
        /* An empty list renders the field with no options; the form refuses an empty selection. */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { cities, openJobs, loading };
};

/** "Fewest open jobs: Nagpur (1), Madurai (2), Indore (3)", the hint under the city picker. */
export const fewestOpenJobsHint = (openJobs: Record<string, number>, shown = 5): string | undefined => {
  const ranked = Object.entries(openJobs).sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0])).slice(0, shown);
  if (!ranked.length) return undefined;
  return `Fewest open jobs: ${ranked.map(([city, n]) => `${city} (${n})`).join(', ')}`;
};
